import * as React from 'react';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { typeLabel, type OfferLike } from './offerKinds';

/** The offer types present in a list, by the label rows show, with how many of each. */
export function offerTypeOptions(offers: OfferLike[]) {
  const counts = new Map<string, number>();
  offers.forEach((offer) => {
    const label = typeLabel(offer);
    if (label) counts.set(label, (counts.get(label) ?? 0) + 1);
  });
  return [...counts.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([label, count]) => ({ label, count }));
}

/** True when no types are picked, or the offer is one of them. */
export const matchesTypes = (offer: OfferLike, types: string[]) =>
  types.length === 0 || types.includes(typeLabel(offer));

/**
 * Narrow a list to some offer types. Lists only the types the list actually holds; picking none
 * shows every type.
 */
export default function OfferTypeFilter({
  offers,
  value,
  onChange,
  label = 'Offer types',
}: {
  offers: OfferLike[];
  value: string[];
  onChange: (types: string[]) => void;
  label?: string;
}) {
  const options = React.useMemo(() => offerTypeOptions(offers), [offers]);
  // A picked type the list no longer holds (the library switched kind) stops counting.
  const selected = value.filter((type) => options.some((option) => option.label === type));
  return (
    <Select<string[]>
      multiple
      displayEmpty
      size='small'
      value={selected}
      onChange={(event) => {
        const next = event.target.value;
        const types = typeof next === 'string' ? next.split(',') : next;
        onChange(types.includes('__all') ? [] : types);
      }}
      renderValue={(types) =>
        types.length === 0 ? 'All types' : types.length === 1 ? types[0] : `${types.length} types`
      }
      inputProps={{ 'aria-label': label }}
      sx={{ minWidth: 132, flex: '0 0 auto', '& .MuiSelect-select': { py: 0.875 } }}
      MenuProps={{ slotProps: { paper: { sx: { maxHeight: 360 } } } }}
    >
      <MenuItem value='__all' dense>
        <ListItemText primary='All types' />
      </MenuItem>
      <Divider />
      {options.map((option) => (
        <MenuItem key={option.label} value={option.label} dense>
          <Checkbox size='small' checked={selected.includes(option.label)} sx={{ p: 0.5, mr: 1 }} />
          <ListItemText
            primary={option.label}
            secondary={`${option.count}`}
            slotProps={{ secondary: { component: 'span', sx: { ml: 1 } } }}
            sx={{ display: 'flex', alignItems: 'baseline' }}
          />
        </MenuItem>
      ))}
    </Select>
  );
}
